import React, { useEffect, useState } from "react";
import { UsersRound } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { usePermission } from "@/contexts/RoleAccessContext";
import { useTeam } from "@/contexts/TeamContext";
import { getManagerDuties, updateManagerDuties } from "@/services/adminService";
import { getErrorMessage } from "@/utils/errorHandler";

const DUTIES = [
  {
    key: "leave",
    label: "Leave approvals",
    description:
      "Managers approve or reject their team's leave first; an admin gives the final decision. Off: requests go straight to admins.",
  },
  {
    key: "appraisals",
    label: "Appraisals & exit interviews",
    description:
      "Managers assign appraisals to their team, write the manager review, and approve exit interviews before an admin does. Off: admins do the manager's part.",
  },
  {
    key: "pips",
    label: "Performance improvement plans",
    description:
      "Managers create and run PIPs for their team. Off: only admins handle PIPs.",
  },
  {
    key: "goals",
    label: "Goals & OKRs",
    description:
      "Managers set and update goals for their team. Off: only admins manage goals.",
  },
];

/**
 * What managers do for their direct reports. A manager is whoever employees
 * report to — any login, employee or admin; their access role doesn't matter.
 */
export default function ManagerDutiesCard() {
  const { can } = usePermission();
  const canEdit =
    can("settings_company_configuration", "edit") || can("department_configuration", "edit");
  const team = useTeam();

  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getManagerDuties()
      .then((duties) => {
        setSaved(duties);
        setDraft(duties);
      })
      .catch((error) => toast.error(getErrorMessage(error, "Failed to load manager responsibilities")));
  }, []);

  const dirty = saved && draft && DUTIES.some(({ key }) => saved[key] !== draft[key]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const duties = await updateManagerDuties(draft);
      setSaved(duties);
      setDraft(duties);
      team.refresh();
      toast.success("Manager responsibilities saved");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save manager responsibilities"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UsersRound className="h-5 w-5 text-primary" />
          Manager responsibilities
        </CardTitle>
        <CardDescription>
          A manager is whoever an employee reports to — anyone with a login,
          employee or admin. Choose what managers handle for their team; access
          roles don't change this.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!draft ? (
          <div className="space-y-3">
            {DUTIES.map(({ key }) => (
              <Skeleton key={key} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            {DUTIES.map(({ key, label, description }) => (
              <div key={key} className="flex items-start justify-between gap-4 rounded-lg border p-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">{description}</p>
                </div>
                <Switch
                  checked={Boolean(draft[key])}
                  onCheckedChange={(checked) => setDraft((prev) => ({ ...prev, [key]: checked }))}
                  disabled={!canEdit || saving}
                  aria-label={label}
                />
              </div>
            ))}
            {canEdit && (
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={!dirty || saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
